'use client'

import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import {
	ALL,
	DEFAULT_ORDER,
	ORDER_OPTIONS,
} from 'product-list/context/ProductListFiltersProvider/constants'
import { useProductListFiltersProvider } from 'product-list/context/ProductListFiltersProvider/useProductListFiltersProvider'
import { getFilterId } from 'product-list/context/ProductListFiltersProvider/utils'
import {
	FilterId,
	FilterType,
	type RangePickerValue,
	type UserFilter,
} from 'product-list/types/Filters'
import type { OrderMethod } from 'product-list/types/ProductsList'
import { getCurrencyRangeString } from 'product-list/utils/getCurrencyRangeString'
import { dataTestIds } from 'product-list/utils/testIds'
import { useMemo, useState } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import type { CountryConfiguration } from 'types/countryConfiguration'
import { Channel } from 'types/masterData'

import { Options } from '../../Options/Options'
import { RangePicker } from '../../RangePicker/RangePicker'
import { Accordion } from '../Accordion/Accordion'

import styles from './FiltersAccordions.module.scss'

function getFilterSelection(
	filter: UserFilter,
	selectedOrder: string,
	country: CountryConfiguration,
	t: (key: string) => string,
	selectedRange?: RangePickerValue,
	selectedLimitRange?: RangePickerValue
): string[] | undefined {
	if (filter.type === FilterType.order) {
		const current = ORDER_OPTIONS.filter(
			(option) => option.id !== DEFAULT_ORDER
		).find((orderItem) => orderItem.id === selectedOrder)
		return [t(current?.label ?? '')]
	}

	if (filter.type === FilterType.price) {
		const isUserSelection =
			selectedLimitRange?.max !== selectedRange?.max ||
			selectedLimitRange?.min !== selectedRange?.min
		return isUserSelection && selectedRange && country
			? [getCurrencyRangeString(selectedRange, country, t)]
			: undefined
	}

	return filter?.selection.filter((selection) => selection !== ALL)
}

export function FiltersAccordions() {
	const { country, channel } = useMasterData()

	const {
		nActiveFilters,
		orderFilter,
		selectedRange,
		selectedOrder,
		selectedLimitRange,
		disabledValues,
		setSelectedOrder,
		setSelectedRange,
		hasSelection,
		toggle,
		selectedFilters: filters,
	} = useProductListFiltersProvider()

	const { isTouchable } = useResponsive()

	const defaultOpenFilter = useMemo(() => {
		if (nActiveFilters > 0 || isTouchable || channel === Channel.Outlet) {
			return ''
		}
		const filter = filters.find((f) => f.type === FilterType.colorGroups)
		return filter ? getFilterId(filter) : ''
	}, [channel, filters, isTouchable, nActiveFilters])

	const [expandedId, setExpandedId] = useState(defaultOpenFilter)
	const { t } = useLabels()

	const orderFilterId = getFilterId(orderFilter)

	return (
		<>
			{!hasSelection && <div className={styles.filler} role='presentation' />}
			{filters.map((filter) => {
				const { type, label } = filter
				if (
					!isTouchable &&
					type === FilterType.generic &&
					filter.id === FilterId.subfamilies
				) {
					return null
				}
				const idSelector = getFilterId(filter)
				return (
					<Accordion
						key={type}
						type={type}
						idSelector={idSelector}
						label={t(label)}
						expandedId={expandedId}
						setExpandedId={setExpandedId}
						options={filter?.options}
						selection={getFilterSelection(
							filter,
							selectedOrder,
							country,
							t,
							selectedRange,
							selectedLimitRange
						)}
						dataTestId={dataTestIds.plpFilterMobileType(type)}
					>
						{selectedLimitRange &&
							selectedRange &&
							type === FilterType.price && (
								<RangePicker
									setSelectedRange={setSelectedRange}
									selectedRange={selectedRange}
									selectedLimitRange={selectedLimitRange}
								/>
							)}
						{filter.options.length > 0 && (
							<Options
								disabledValues={disabledValues}
								isMobile
								userFilter={{
									...filter,
									options: filter.options.map((option) => ({
										...option,
										label: t(option.label),
									})),
								}}
								a11yLabel={t(label)}
								onChange={(typeE, value, id) => {
									toggle(typeE, value, id)
								}}
							/>
						)}
					</Accordion>
				)
			})}
			{orderFilter && (
				<Accordion
					key={orderFilterId}
					type={orderFilter.type}
					idSelector={orderFilterId}
					label={t(orderFilter.label)}
					expandedId={expandedId}
					setExpandedId={setExpandedId}
					selection={getFilterSelection(
						orderFilter,
						selectedOrder,
						country,
						t,
						selectedRange
					)}
					dataTestId={dataTestIds.plpFilterMobileType(orderFilter.type)}
				>
					<Options
						disabledValues={disabledValues}
						isMobile
						userFilter={{ ...orderFilter, selection: [selectedOrder] }}
						a11yLabel={t(orderFilter.label)}
						onChange={(_type, value) => {
							setSelectedOrder(value as OrderMethod)
						}}
					/>
				</Accordion>
			)}
		</>
	)
}
